import React, { useState, useEffect, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useStorageDeletedRecentUser } from "libs/storage/adapters";
import { cn } from "@/lib/utils";

import AccountCombobox from "../AccountCombobox";

import { initCreatorFeed, searchCreator } from "actions/sourceMedia";
import { updateLibraryUrl } from "libs/media-library/url-sync";
import { getAccountsForPlatform } from "selectors/skedCoreSelectors";
import { selectIgAccount } from "actions/sourceMedia";
import { handleError } from "utils/handleError";
import {
  getRecentSearchCreators,
  getSelectedCreator,
  getSelectedIgAccount,
} from "selectors/hashtagFeedSelectors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useTrackEvent } from "libs/analytics";
import { LIBRARY_SEARCH_INFLUENCER } from "constants/Events";
import { getLoading, getRoutinesLoading } from "selectors/commonSelectors";
import {
  SEARCH_CREATOR,
  SELECT_RECENT_SEARCH_CREATORS,
} from "constants/ActionTypes";
import { InstagramUser } from "features/media-library/components/media-picker/types";
import { AccountType } from "shared/types/accounts";
import type { ReduxRootState } from "types/redux-store";

export function useSearchIgCreators() {
  const dispatch = useDispatch();
  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const recentCreators: InstagramUser[] = useSelector(getRecentSearchCreators);
  const selectedCreator = useSelector(getSelectedCreator);
  const [deletedCreatorIds, setDeletedCreatorIds] =
    useStorageDeletedRecentUser(null);

  const filteredCreators = React.useMemo(
    () =>
      recentCreators.filter((user) => !deletedCreatorIds?.includes(user._id)),
    [recentCreators, deletedCreatorIds]
  );

  const loading = useSelector(getLoading);
  const loadingCreators = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [SELECT_RECENT_SEARCH_CREATORS, SEARCH_CREATOR])
  );

  const promisifyAction = (action: any, args: any[]) => {
    return new Promise((resolve) => {
      dispatch(action(...args, resolve));
    });
  };

  const requestCreator = async (searchQuery: string) => {
    let searchedCreator: InstagramUser | undefined;

    if (searchQuery && selectedIgAccount?._id) {
      try {
        // lowercase as the IG API is case sensitive
        searchQuery = searchQuery.toLowerCase();
        searchedCreator = (await promisifyAction(searchCreator, [
          selectedIgAccount._id,
          searchQuery,
        ])) as InstagramUser | undefined;

        if (!searchedCreator) {
          throw new Error("Can not get user result");
        }

        const updatedCreators = cloneDeep(recentCreators);
        updatedCreators.pop();
        updatedCreators.unshift(searchedCreator);

        //remove newly added id from deletedUserIds if any
        if (deletedCreatorIds) {
          setDeletedCreatorIds(
            deletedCreatorIds?.filter((id) => id !== searchedCreator?._id)
          );
        }
      } catch (error) {
        handleError(
          error,
          `Could not get information for ${searchQuery} - this user does not exist, or is not a Business or Creator account.`
        );
      }
    }

    return searchedCreator;
  };

  const handleRemoveCreator = (userId: string) => {
    setDeletedCreatorIds(
      deletedCreatorIds ? [...deletedCreatorIds, userId] : [userId]
    );
  };

  return {
    isLoading: loading || loadingCreators,
    recentCreators: filteredCreators,
    selectedCreator,
    requestCreator,
    handleRemoveCreator,
  };
}

function CreatorSearchForm() {
  const trackEvent = useTrackEvent();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedIgAccount = useSelector(
    getSelectedIgAccount
  ) as AccountType | null;
  const { requestCreator, isLoading, recentCreators, handleRemoveCreator } =
    useSearchIgCreators();

  const [searchInputText, setSearchInputText] = useState("");
  const [highlightedCreatorId, setHighlightedCreatorId] = useState<string>();
  const [isSearching, setIsSearching] = useState(false);

  const openCreatorContent = (user: InstagramUser) => {
    setHighlightedCreatorId(user._id);
    dispatch(initCreatorFeed({ user, accountId: selectedIgAccount?._id }));

    // Update URL with selected creator
    const newParams = updateLibraryUrl(searchParams, {
      tab: "creator",
      account: selectedIgAccount?._id,
      creator: user._id,
    });
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const handleSearchCreator = useDebouncedCallback(async () => {
    if (!searchInputText.trim()) return;

    setIsSearching(true);
    trackEvent({
      eventName: LIBRARY_SEARCH_INFLUENCER,
      useServices: ["AMP"],
      metadata: { location: "library" },
    });
    const searchedCreator = await requestCreator(searchInputText);
    setSearchInputText("");
    setIsSearching(false);
    searchedCreator && openCreatorContent(searchedCreator);
  }, 300);

  // Don't show search form if no account selected
  if (!selectedIgAccount) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">
            @
          </span>
          <Input
            placeholder="Search username..."
            value={searchInputText}
            onChange={(e) => setSearchInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearchCreator();
              }
            }}
            className="flex-1 h-9 pl-7 text-sm border-neutral-200 bg-white"
          />
        </div>
        <Button
          variant="black-primary"
          size="sm"
          className="h-9 px-3"
          onClick={() => handleSearchCreator()}
          disabled={isSearching || !searchInputText.trim()}
        >
          {isSearching ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <i className="fa-solid fa-arrow-right" />
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-neutral-500">
          <Spinner className="h-4 w-4" />
          <span className="text-sm">Loading...</span>
        </div>
      ) : recentCreators.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
            Recent searches
          </p>
          {recentCreators.map((user) => {
            const isSelected = highlightedCreatorId === user._id;
            return (
              <button
                key={user._id}
                type="button"
                onClick={() => openCreatorContent(user)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer",
                  "font-inter text-sm text-neutral-700 transition-all duration-150",
                  "relative group",
                  isSelected
                    ? "bg-blue-50 text-neutral-900 font-medium border-l-[3px] border-blue-500 rounded-r-lg pl-[9px]"
                    : "hover:bg-white hover:text-neutral-900 hover:shadow-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                )}
              >
                <img
                  src={user.data?.profile_picture_url}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = `https://imggen.skedsocial.com/api/v3/${user.userName
                      .charAt(0)
                      .toUpperCase()}.jpg?target=profile`;
                  }}
                  alt={`Avatar for ${user.userName}`}
                  className="w-6 h-6 rounded-full object-cover"
                />
                <p className="m-0 flex-1 text-left truncate">{user.userName}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCreator(user._id);
                  }}
                  className={cn(
                    "opacity-0 group-hover:opacity-100 transition-opacity",
                    "text-neutral-400 hover:text-red-500",
                    "focus-visible:opacity-100"
                  )}
                  aria-label={`Remove ${user.userName}`}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const UserSelector = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accounts = useSelector((state: any) =>
    getAccountsForPlatform(state, "IG")
  ) as AccountType[];
  const selectedIgAccount = useSelector(
    getSelectedIgAccount
  ) as AccountType | null;

  // Track mounted state and debounce timer for cleanup
  const isMountedRef = useRef(true);
  const initTimeoutRef = useRef<NodeJS.Timeout>();
  const lastInitializedAccountRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, []);

  // Auto-select first account if none selected
  useEffect(() => {
    if (!selectedIgAccount && accounts?.length > 0) {
      dispatch(selectIgAccount(accounts[0]._id));
    }
  }, [selectedIgAccount, accounts, dispatch]);

  // Debounced init of creator feed - only when account actually changes
  useEffect(() => {
    // Clear any pending timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    const accountId = selectedIgAccount?._id;
    if (accountId && accountId !== lastInitializedAccountRef.current) {
      initTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          lastInitializedAccountRef.current = accountId;
          dispatch(initCreatorFeed({ accountId }));
        }
      }, 300); // 300ms debounce
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to trigger on accountId changes, not selectedIgAccount object changes
  }, [selectedIgAccount?._id, dispatch]);

  const handleAccountChange = (account: AccountType) => {
    dispatch(selectIgAccount(account._id));

    // Update URL with selected account
    const newParams = updateLibraryUrl(searchParams, {
      tab: "creator",
      account: account._id,
    });
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  return (
    <div className="px-3 py-1">
      <div className="space-y-2">
        <AccountCombobox
          accounts={accounts}
          onSelect={handleAccountChange}
          selectedAccountId={selectedIgAccount?._id}
        />
        <CreatorSearchForm />
      </div>
    </div>
  );
};

export default UserSelector;
